import React from "react";
import { Link } from "react-router-dom";

/**
 * The blur-and-upgrade treatment used wherever a subscriber meets something
 * their plan does not include: commercial property cards, the Investor
 * Portfolio filter, the owner drill-down, and the portfolio tiles on a
 * property's detail page.
 *
 * Callers pass `message` from upgradeMessage(memData, …) rather than writing
 * the sentence inline, because whether "Upgrade to Rhino Legend" is even true
 * depends on the member's plan.
 *
 * It sits on top of the real content rather than replacing it — the product
 * rule is that the feature stays VISIBLE so the customer can see what they are
 * being offered, just not usable and with no live numbers showing through.
 * Callers are responsible for the `position: relative` container and for
 * actually disabling any inputs underneath.
 *
 * That container MUST also be a stacking context — `position: relative` plus an
 * explicit `z-index` (0 is right for most callers). A bare `position: relative`
 * parent has `z-index: auto` and creates NO stacking context, so the overlay's
 * own z-index below competes in whatever ancestor context it lands in — the
 * root, usually. A lock chip inside a search-result card then paints straight
 * over the open filter dropdown (`.drop_cnt`, z-index 2) and the filter bar,
 * which is exactly the bug this note exists to stop recurring. Isolating the
 * container keeps the overlay above its own cell and below everything else.
 *
 * Two shapes, because the locked controls are not all the same size:
 *
 *   variant="card"   (default) a full upsell card. Only for blocks tall enough
 *                    to hold one — a slider, an input pair, a property tile.
 *   variant="inline" a single-line lock chip. For short rows like one checkbox,
 *                    where a card is taller than the thing it is locking and
 *                    would spill over whatever sits above and below it.
 *
 * The root clips its own children (`overflow: hidden`). An overlay that paints
 * outside its container does not just look wrong — it covers and swallows
 * clicks on neighbouring controls that the customer IS entitled to use.
 */

// Matches .site_btn / .drop_cnt in App.css. Root font-size is 10px.
const BRAND = "#67773f";
const MUTED = "rgba(0, 0, 0, 0.6)";
const PANEL_SHADOW =
  "0 0.7rem 3.5rem -0.5rem rgba(29, 41, 0, 0.1), 0 -0.5rem 1rem -0.6rem rgba(29, 41, 0, 0.1)";

export default function UpgradeOverlay({
  title = "Commercial Properties",
  // Names no plan on purpose. An agreement can exclude a capability from any
  // plan, so a caller that does not compute the line (see upgradeMessage)
  // must not be the one telling a Legend customer to buy Legend.
  message = "Not included on your plan.",
  ctaLabel = "View Plans",
  ctaTo = "/pricing",
  blur = 3,
  compact = false,
  variant = "card",
  testId = "upgrade-overlay",
}) {
  const root = {
    position: "absolute",
    inset: 0,
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    backdropFilter: `blur(${blur}px)`,
    WebkitBackdropFilter: `blur(${blur}px)`,
    display: "flex",
    alignItems: "center",
    borderRadius: "inherit",
    zIndex: 10,
    cursor: "not-allowed",
    // Never paint outside the control being locked.
    overflow: "hidden",
  };

  if (variant === "inline") {
    return (
      <div
        data-testid={testId}
        aria-hidden="false"
        style={{ ...root, justifyContent: "flex-end", padding: "0 0.4rem" }}
      >
        <Link
          to={ctaTo}
          title={`${title} — ${message}`}
          aria-label={`${title}. ${message}. ${ctaLabel}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.4rem",
            maxWidth: "100%",
            backgroundColor: "rgba(103, 119, 63, 0.1)",
            color: BRAND,
            fontFamily: "Bold",
            fontSize: "1.1rem",
            letterSpacing: "0.03rem",
            padding: "0.3rem 0.8rem",
            borderRadius: "0.4rem",
            textDecoration: "none",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            cursor: "pointer",
          }}
        >
          <span aria-hidden="true">&#128274;</span>
          {ctaLabel}
        </Link>
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      aria-hidden="false"
      style={{ ...root, flexDirection: "column", justifyContent: "center" }}
    >
      {/* Compact lays the card out as a row. Stacked, it is taller than the
          filter block it covers, and an oversized card either spills onto the
          neighbouring filters or gets clipped by the root. */}
      <div
        style={{
          display: "flex",
          flexDirection: compact ? "row" : "column",
          alignItems: "center",
          gap: compact ? "1rem" : 0,
          textAlign: compact ? "left" : "center",
          background: "#fff",
          padding: compact ? "0.6rem 0.8rem" : "1.6rem 2rem",
          border: "1px solid rgba(103, 119, 63, 0.15)",
          borderRadius: "0.7rem",
          boxShadow: PANEL_SHADOW,
          maxWidth: "100%",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <p
            style={{
              margin: compact ? 0 : "0 0 0.6rem 0",
              color: "#000929",
              fontFamily: "Bold",
              fontSize: compact ? "1.2rem" : "1.5rem",
            }}
          >
            {title}
          </p>
          <p
            style={{
              margin: compact ? 0 : "0 0 1.4rem 0",
              color: MUTED,
              fontFamily: "Regular",
              fontSize: compact ? "1.1rem" : "1.3rem",
            }}
          >
            {message}
          </p>
        </div>
        <Link
          to={ctaTo}
          className="site_btn"
          style={{
            display: "inline-flex",
            flexShrink: 0,
            height: compact ? "2.8rem" : "3.8rem",
            padding: compact ? "0 1.2rem" : "0 1.8rem",
            fontSize: compact ? "1.2rem" : "1.4rem",
          }}
        >
          {ctaLabel}
        </Link>
      </div>
    </div>
  );
}
